import { computed, unref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { selectedAccommodationId } from '@/layouts/queries'

import DashboardService from '@/services/DashboardService'

const days = {
  daily: 'DAY',
  weekly: 'WEEK',
  monthly: 'MONTH',
}

function buildGraphObject(data) {
  const keyMap = {
    clickThroughRate: 'clickThroughRate',
    clicks: 'clicks',
    impressions: 'impressions',
  }

  const result = {}

  Object.entries(data).forEach(([key, value]) => {
    const newKey = keyMap[key] || key
    result[newKey] = value.graph || []
  })

  return result
}

const getPartnersNames = async ({ partnerIds }) => {
  const response = await DashboardService.fetchPartnersName(partnerIds)
  return response?.data?.data?.getAdvertiserDetails?.advertiserDetails
}

export function usePartnerNames(partnerIds) {
  const isQueryEnabled = computed(() => {
    const ids = unref(partnerIds)
    return Array.isArray(ids) && ids.length > 0
  })
  const { data, isError, isLoading, isFetching } = useQuery({
    enabled: isQueryEnabled,
    queryKey: ['partnerNames', partnerIds],
    queryFn: ({ queryKey }) =>
      getPartnersNames({
        partnerIds: queryKey[1],
      }),
  })

  const partners = computed(() => data?.value ?? [])

  return {
    partners,
    isError,
    isLoading,
    partnerMapIsFetching: isFetching,
  }
}

function useConversionFunnelQuery(params) {
  const queryKey = computed(() => [params[0], unref(params[1]), unref(params[2]), unref(params[3]), unref(params[4])])
  const { data, isError, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: ({ queryKey }) =>
      DashboardService.fetchConversionFunnel({
        accommodationId: queryKey[1],
        startDate: queryKey[2],
        endDate: queryKey[3],
        breakout: days[queryKey[4]],
      }),
  })

  const conversionFunnel = computed(() => data?.value?.data)
  const views = computed(() => conversionFunnel.value?.impressions?.value)
  const visits = computed(() => conversionFunnel.value?.clicks?.value)

  return { data, conversionFunnel, views, visits, isError, isLoading, isFetching }
}

export function useConversionFunnelData(startDate, endDate, breakout) {
  const { conversionFunnel, views, visits, isError, isLoading, isFetching } = useConversionFunnelQuery([
    'conversionFunnel',
    selectedAccommodationId,
    startDate,
    endDate,
    breakout,
  ])

  const viewsVariation = computed(() => conversionFunnel.value?.impressions?.variation?.variationOverPeriod || 0)
  const visitsVariation = computed(() => conversionFunnel.value?.clicks?.variation?.variationOverPeriod || 0)
  const partnerDistribution = computed(() => conversionFunnel.value?.clicks?.partners)

  const graphData = computed(() => {
    return {
      clickThroughRate: { graph: conversionFunnel.value?.clickThroughRate?.graph },
      clicks: { graph: conversionFunnel.value?.clicks?.graph },
      impressions: { graph: conversionFunnel.value?.impressions?.graph },
    }
  })

  const formattedGraphData = computed(() =>
    buildGraphObject(graphData.value, {
      interval: unref(breakout),
      startDate: unref(startDate),
      endDate: unref(endDate),
    })
  )

  return {
    conversionFunnel,
    views,
    viewsVariation,
    visits,
    visitsVariation,
    partnerDistribution,
    isError,
    isLoading,
    isFetching,
    formattedGraphData,
  }
}

export function useConversionFunnelDataDefault(startDate, endDate, breakout) {
  const { views, visits, isError, isLoading, isFetching } = useConversionFunnelQuery([
    'conversionFunnelDefault',
    selectedAccommodationId,
    startDate,
    endDate,
    breakout,
  ])
  const hasNoInitialData = computed(() => !views.value && !visits.value)
  return {
    hasNoInitialData,
    isError,
    isLoading,
    isFetching,
  }
}
